// Home EFECTIVO del usuario — getHomeForRole (por rol de plataforma) MÁS la
// regla de "personal dedicado a mantenimiento" (decisión de producto
// 2026-07-05): rol base 'lider' (mínimo privilegio de producción) + capability
// `puede_ver_mantenimiento` + algún rol de módulo en usuarios_mantenimiento →
// su mundo es /mantenimiento, no la captura.
//
// Es un HOOK (no función pura como getHomeForRole) porque la regla necesita
// /api/me: roles_mtto y la capability no viajan en el JWT. Montarlo solo con
// sesión activa (HomePage, guards, layouts) — useMe dispara el fetch.
//
// La regla exige la capability ADEMÁS del rol de módulo a propósito: si a un
// dedicado le revocan `puede_ver_mantenimiento`, su home vuelve a /capturar y
// el guard RequireMantenimiento no entra en loop de redirects.

import { useMe } from '../api/queries';
import { getHomeForRole } from './getHomeForRole';
import { useAuth } from './useAuth';
import { useVerComo, homeVerComo } from '../ver-como';

/** true si el usuario es personal DEDICADO a mantenimiento (regla de arriba). */
export function useMantenimientoDedicado(): boolean {
  const { state } = useAuth();
  const me = useMe();
  const { estado: verComo } = useVerComo();
  // En modo "Ver como" mantenimiento se entra al mundo dedicado del módulo
  // (sidebar propio), tal cual lo ve el técnico/coordinador de mtto.
  if (verComo) return verComo.destino === 'mantenimiento';
  return (
    state.kind === 'signed_in' &&
    state.claims.rol === 'lider' &&
    (me.data?.ctx.puede_ver_mantenimiento ?? false) &&
    (me.data?.ctx.roles_mtto.length ?? 0) > 0
  );
}

export interface HomePathResult {
  home: string;
  /**
   * true mientras /api/me carga Y la respuesta puede cambiar el home (solo
   * pasa con rol 'lider'). El caller debe mostrar loading en vez de navegar,
   * o el dedicado aterrizaría un instante en /capturar.
   */
  resolviendo: boolean;
}

export function useHomePath(): HomePathResult {
  const { state } = useAuth();
  const me = useMe();
  const { estado: verComo } = useVerComo();
  // En modo "Ver como", el home efectivo es el del mundo suplantado.
  if (verComo) return { home: homeVerComo(verComo.destino), resolviendo: false };
  if (state.kind !== 'signed_in') return { home: '/', resolviendo: false };
  const rol = state.claims.rol;
  if (rol === 'lider') {
    if (me.isLoading) return { home: getHomeForRole(rol), resolviendo: true };
    const ctx = me.data?.ctx;
    if (ctx && ctx.puede_ver_mantenimiento && ctx.roles_mtto.length > 0) {
      return { home: '/mantenimiento', resolviendo: false };
    }
    // me con error → degradar al home clásico del rol (no bloquear el login).
  }
  return { home: getHomeForRole(rol), resolviendo: false };
}
